import { Controller } from "@hotwired/stimulus"

/**
 * Image Comparison Controller
 *
 * Draggable before/after slider for comparing two images.
 * Features:
 * - Click/drag anywhere on image to control slider position
 * - Drag handle for precise control
 * - Optional sync across multiple controllers via syncGroup value
 * - Keyboard support (arrow keys when focused)
 * - Touch direction detection: horizontal swipes control slider, vertical swipes scroll page
 */
export default class extends Controller<HTMLElement> {
  static targets = ["before", "after", "handle", "beforeImage", "afterImage"]
  static values = {
    position: { type: Number, default: 50 },
    syncGroup: { type: String, default: "" },
    hint: { type: Boolean, default: false }
  }

  declare readonly beforeTarget: HTMLElement
  declare readonly afterTarget: HTMLElement
  declare readonly handleTarget: HTMLElement
  declare readonly beforeImageTarget: HTMLElement
  declare readonly afterImageTarget: HTMLElement

  declare positionValue: number
  declare syncGroupValue: string
  declare hintValue: boolean

  declare readonly hasBeforeTarget: boolean
  declare readonly hasAfterTarget: boolean
  declare readonly hasHandleTarget: boolean

  private isDragging = false
  private containerRect: DOMRect | null = null
  private containerWidth = 0
  private hasInteracted = false
  private hintAnimationFrame: number | null = null

  // Drag coalescing: at most one position update per frame.
  private rafPending: number | null = null
  private pendingClientX: number | null = null

  // Touch direction detection - allows vertical scroll while capturing horizontal swipes
  private touchStartX = 0
  private touchStartY = 0
  private directionLocked: "horizontal" | "vertical" | null = null

  private boundHandleMouseMove = this.handleMouseMove.bind(this)
  private boundHandleMouseUp = this.handleMouseUp.bind(this)
  private boundHandleTouchMove = this.handleTouchMove.bind(this)
  private boundHandleTouchEnd = this.handleTouchEnd.bind(this)
  private boundHandleContainerTouchMove = this.handleContainerTouchMove.bind(this)
  private boundHandleContainerTouchEnd = this.handleContainerTouchEnd.bind(this)
  private boundHandleSyncEvent = this.handleSyncEvent.bind(this)

  // Shared observer that gates the loading-pulse animation to in/near-viewport
  // tiles. Below-fold lazy tiles never load their "after" image, so without
  // this they'd run the keyframe forever. The CSS only animates the placeholder
  // under `.image-comparison--pulsing`, so the default (unobserved) state is
  // paused.
  private pulseObserver: IntersectionObserver | null = null

  connect(): void {
    this.updatePosition(this.positionValue)
    this.watchImageLoads()
    this.observePulse()

    // Listen for sync events from other controllers
    if (this.syncGroupValue) {
      window.addEventListener("image-comparison:sync", this.boundHandleSyncEvent)
    }
  }

  disconnect(): void {
    this.cancelHintAnimation()
    this.cancelScheduledUpdate()
    this.cleanupListeners()
    this.disconnectPulse()
    if (this.syncGroupValue) {
      window.removeEventListener("image-comparison:sync", this.boundHandleSyncEvent)
    }
  }

  // Gate the loading pulse to near-viewport tiles. Once the after image is
  // loaded the placeholder is hidden (--after-loaded), so we also stop pulsing
  // then to keep the keyframe off any settled tile.
  private observePulse(): void {
    if (this.element.classList.contains("image-comparison--after-loaded")) return
    if (typeof IntersectionObserver === "undefined") {
      // No IO support: fall back to always pulsing so the placeholder still
      // animates while loading (matches the old behavior).
      this.element.classList.add("image-comparison--pulsing")
      return
    }

    this.pulseObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        this.element.classList.toggle("image-comparison--pulsing", entry.isIntersecting)
      }
    }, { rootMargin: "200px" })
    this.pulseObserver.observe(this.element)
  }

  private disconnectPulse(): void {
    if (this.pulseObserver) {
      this.pulseObserver.disconnect()
      this.pulseObserver = null
    }
  }

  // Watch both images for load, then trigger the hint animation
  private watchImageLoads(): void {
    const beforeImg = this.hasBeforeTarget ? this.beforeTarget.querySelector("img") : null
    const afterImg = this.hasAfterTarget ? this.afterTarget.querySelector("img") : null

    let beforeLoaded = !beforeImg || (beforeImg.complete && beforeImg.naturalWidth > 0)
    let afterLoaded = !afterImg || (afterImg.complete && afterImg.naturalWidth > 0)

    if (afterLoaded) {
      this.markAfterLoaded()
    }

    const onBothLoaded = () => {
      if (beforeLoaded && afterLoaded) {
        setTimeout(() => this.playHintAnimation(), 400)
      }
    }

    if (beforeLoaded && afterLoaded) {
      onBothLoaded()
      return
    }

    if (!beforeLoaded && beforeImg) {
      beforeImg.addEventListener("load", () => {
        beforeLoaded = true
        onBothLoaded()
      }, { once: true })
    }

    if (!afterLoaded && afterImg) {
      afterImg.addEventListener("load", () => {
        afterLoaded = true
        this.markAfterLoaded()
        onBothLoaded()
      }, { once: true })
    }
  }

  // The after image is in place: hide the loading placeholder and stop gating
  // the (now invisible) pulse — no point observing a settled tile.
  private markAfterLoaded(): void {
    this.element.classList.add("image-comparison--after-loaded")
    this.element.classList.remove("image-comparison--pulsing")
    this.disconnectPulse()
  }

  // Mouse events - handle clicks anywhere on the container
  handleContainerMouseDown(event: MouseEvent): void {
    event.preventDefault()
    this.startDrag()
    // Immediately move slider to click position
    this.updateFromClientX(event.clientX)
    document.addEventListener("mousemove", this.boundHandleMouseMove)
    document.addEventListener("mouseup", this.boundHandleMouseUp)
  }

  // Mouse events on handle (legacy, also works)
  handleMouseDown(event: MouseEvent): void {
    event.preventDefault()
    this.startDrag()
    document.addEventListener("mousemove", this.boundHandleMouseMove)
    document.addEventListener("mouseup", this.boundHandleMouseUp)
  }

  private handleMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return
    this.scheduleUpdate(event.clientX)
  }

  private handleMouseUp(): void {
    this.endDrag()
    document.removeEventListener("mousemove", this.boundHandleMouseMove)
    document.removeEventListener("mouseup", this.boundHandleMouseUp)
  }

  // Touch events - handle touches anywhere on the container
  // Uses direction detection: horizontal swipes control slider, vertical swipes scroll page
  handleContainerTouchStart(event: TouchEvent): void {
    if (event.touches.length !== 1) return

    // Record start position for direction detection - DON'T prevent default yet
    this.touchStartX = event.touches[0].clientX
    this.touchStartY = event.touches[0].clientY
    this.directionLocked = null
    this.containerRect = this.element.getBoundingClientRect()

    document.addEventListener("touchmove", this.boundHandleContainerTouchMove, { passive: false })
    document.addEventListener("touchend", this.boundHandleContainerTouchEnd)
  }

  private handleContainerTouchMove(event: TouchEvent): void {
    if (event.touches.length !== 1) return

    const touch = event.touches[0]
    const deltaX = Math.abs(touch.clientX - this.touchStartX)
    const deltaY = Math.abs(touch.clientY - this.touchStartY)

    // Determine direction on first significant movement
    if (this.directionLocked === null && (deltaX > 5 || deltaY > 5)) {
      this.directionLocked = deltaX > deltaY ? "horizontal" : "vertical"

      if (this.directionLocked === "horizontal") {
        // Start dragging - move slider to current position
        this.hasInteracted = true
        this.cancelHintAnimation()
        this.isDragging = true
        this.containerWidth = this.containerRect?.width ?? this.element.getBoundingClientRect().width
        this.element.classList.add("image-comparison--dragging")
        this.scheduleUpdate(touch.clientX)
      }
    }

    // If horizontal, control the slider
    if (this.directionLocked === "horizontal") {
      event.preventDefault()
      if (this.isDragging) {
        this.scheduleUpdate(touch.clientX)
      }
    }
    // If vertical, do nothing - let browser scroll
  }

  private handleContainerTouchEnd(): void {
    this.endDrag()
    this.directionLocked = null
    document.removeEventListener("touchmove", this.boundHandleContainerTouchMove)
    document.removeEventListener("touchend", this.boundHandleContainerTouchEnd)
  }

  // Touch events on handle - immediate capture (user is explicitly touching handle)
  handleTouchStart(event: TouchEvent): void {
    if (event.touches.length !== 1) return
    event.preventDefault()
    this.startDrag()
    document.addEventListener("touchmove", this.boundHandleTouchMove, { passive: false })
    document.addEventListener("touchend", this.boundHandleTouchEnd)
  }

  private handleTouchMove(event: TouchEvent): void {
    if (!this.isDragging || event.touches.length !== 1) return
    event.preventDefault()
    this.scheduleUpdate(event.touches[0].clientX)
  }

  private handleTouchEnd(): void {
    this.endDrag()
    document.removeEventListener("touchmove", this.boundHandleTouchMove)
    document.removeEventListener("touchend", this.boundHandleTouchEnd)
  }

  // Keyboard navigation
  handleKeydown(event: KeyboardEvent): void {
    this.hasInteracted = true
    this.cancelHintAnimation()
    const step = event.shiftKey ? 10 : 2

    switch (event.key) {
      case "ArrowLeft":
        event.preventDefault()
        this.setPosition(Math.max(0, this.positionValue - step))
        break
      case "ArrowRight":
        event.preventDefault()
        this.setPosition(Math.min(100, this.positionValue + step))
        break
    }
  }

  // Hint animation - nudge slider left and back to show it's interactive
  private playHintAnimation(): void {
    if (this.hasInteracted || !this.hintValue) return

    const start = 50
    const target = 35
    const duration = 1200 // ms total (600ms each way)
    const startTime = performance.now()

    const animate = (now: number) => {
      if (this.hasInteracted) return

      const elapsed = now - startTime
      if (elapsed >= duration) {
        this.setPosition(start)
        this.hintAnimationFrame = null
        return
      }

      const half = duration / 2
      let position: number
      if (elapsed < half) {
        const t = easeInOutCubic(elapsed / half)
        position = start + (target - start) * t
      } else {
        const t = easeInOutCubic((elapsed - half) / half)
        position = target + (start - target) * t
      }

      this.setPosition(position)
      this.hintAnimationFrame = requestAnimationFrame(animate)
    }

    this.hintAnimationFrame = requestAnimationFrame(animate)
  }

  private cancelHintAnimation(): void {
    if (this.hintAnimationFrame !== null) {
      cancelAnimationFrame(this.hintAnimationFrame)
      this.hintAnimationFrame = null
    }
  }

  // Sync handling
  private handleSyncEvent(event: Event): void {
    const customEvent = event as CustomEvent<{ group: string; position: number; source: HTMLElement }>
    if (customEvent.detail.group !== this.syncGroupValue) return
    if (customEvent.detail.source === this.element) return

    // If we're running a hint animation, cancel it and follow the sync leader
    this.cancelHintAnimation()
    this.updatePosition(customEvent.detail.position)
  }

  private broadcastSync(): void {
    if (!this.syncGroupValue) return

    const event = new CustomEvent("image-comparison:sync", {
      detail: {
        group: this.syncGroupValue,
        position: this.positionValue,
        source: this.element
      }
    })
    window.dispatchEvent(event)
  }

  // Core logic
  private startDrag(): void {
    this.hasInteracted = true
    this.cancelHintAnimation()
    this.isDragging = true
    this.containerRect = this.element.getBoundingClientRect()
    this.containerWidth = this.containerRect.width
    this.element.classList.add("image-comparison--dragging")
  }

  private endDrag(): void {
    this.isDragging = false
    // Flush a still-pending coalesced move so release lands on the final
    // position, then drop any scheduled frame.
    if (this.pendingClientX !== null) {
      this.updateFromClientX(this.pendingClientX)
    }
    this.cancelScheduledUpdate()
    this.containerRect = null
    this.element.classList.remove("image-comparison--dragging")
  }

  // Coalesce rapid pointer/touch moves into one update per animation frame, so
  // a burst of move events triggers a single layout + sync broadcast.
  private scheduleUpdate(clientX: number): void {
    this.pendingClientX = clientX
    if (this.rafPending !== null) return
    this.rafPending = requestAnimationFrame(() => {
      this.rafPending = null
      if (this.pendingClientX === null) return
      const x = this.pendingClientX
      this.pendingClientX = null
      // updateFromClientX → setPosition → broadcastSync, so this is the single
      // sync broadcast for the frame.
      this.updateFromClientX(x)
    })
  }

  private cancelScheduledUpdate(): void {
    if (this.rafPending !== null) {
      cancelAnimationFrame(this.rafPending)
      this.rafPending = null
    }
    this.pendingClientX = null
  }

  private cleanupListeners(): void {
    document.removeEventListener("mousemove", this.boundHandleMouseMove)
    document.removeEventListener("mouseup", this.boundHandleMouseUp)
    document.removeEventListener("touchmove", this.boundHandleTouchMove)
    document.removeEventListener("touchend", this.boundHandleTouchEnd)
    document.removeEventListener("touchmove", this.boundHandleContainerTouchMove)
    document.removeEventListener("touchend", this.boundHandleContainerTouchEnd)
  }

  private updateFromClientX(clientX: number): void {
    if (!this.containerRect) return

    const relativeX = clientX - this.containerRect.left
    const percentage = (relativeX / this.containerRect.width) * 100
    const clampedPercentage = Math.max(0, Math.min(100, percentage))

    this.setPosition(clampedPercentage)
  }

  private setPosition(position: number): void {
    this.positionValue = position
    this.updatePosition(position)
    this.broadcastSync()
  }

  private updatePosition(position: number): void {
    this.positionValue = position

    // Set CSS custom property for label opacity calculations
    this.element.style.setProperty("--position", String(position))

    // Update the after layer clip (shows right portion from position% onwards).
    // clipPath is composite-friendly, so it stays a direct write.
    if (this.hasAfterTarget) {
      this.afterTarget.style.clipPath = `inset(0 0 0 ${position}%)`
    }

    // Drive the handle with a transform instead of `left` to avoid a layout on
    // every drag frame. translateX percentages are relative to the element's
    // own width, so resolve to pixels via the container width (cached on drag
    // start; measured lazily otherwise, e.g. connect/sync/keyboard).
    //
    // Measure with offsetWidth, NOT getBoundingClientRect().width: the latter
    // returns the *visually transformed* width. In the lightbox this controller
    // connects while DaisyUI's .modal-box is still mid `scale: .95 → 1` open
    // transition, so getBoundingClientRect would report ~95% of the real width
    // and the handle would land left of the (percentage-based, transform-immune)
    // clip seam until the first drag re-measured. offsetWidth is the
    // untransformed layout width — the same coordinate space translateX paints
    // in — so the handle matches the seam from the first paint.
    if (this.hasHandleTarget) {
      const width = this.containerWidth || this.element.offsetWidth
      const px = (position / 100) * width
      this.handleTarget.style.left = "0"
      this.handleTarget.style.transform = `translateX(${px}px)`
    }
  }
}

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
}
